<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  isCollapsed: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
  countLabel: { type: [String, Number], default: null },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() => {
  if (props.countLabel != null && props.countLabel !== '') {
    return props.countLabel;
  }
  if (dynamicCount.value == null || dynamicCount.value === '') return null;
  return dynamicCount.value > 99 ? '99+' : dynamicCount.value;
});
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex items-center gap-3 rounded-md"
    :class="[
      isCollapsed ? 'px-2 py-2 size-10 justify-center' : 'px-2 py-1.5 h-8',
      {
        'text-n-slate-12 bg-n-alpha-2 font-medium': isActive && !hasActiveChild,
        'text-n-slate-12 font-medium': hasActiveChild,
        'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
      },
    ]"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <Icon v-if="icon" :icon="icon" class="size-5" />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div
      v-if="!isCollapsed"
      class="flex items-center gap-1.5 flex-grow min-w-0"
    >
      <span class="text-base font-medium leading-5 flex-grow truncate">
        {{ label }}
      </span>
      <span
        v-if="count && !expandable"
        class="rounded-md text-xs leading-5 font-medium text-center outline outline-1 px-1 flex-shrink-0 tabular-nums"
        :class="{
          'text-n-slate-12 outline-n-slate-6': isActive,
          'text-n-slate-11 outline-n-strong': !isActive,
        }"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="expandable && !isCollapsed"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-5"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
